import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDate } from '@common/utils';
import { Box, Loader, Table, TableBody, TableHead } from '@base-app';
import { COMMON_TRANSLATION } from '@constants/index';
import { useSequenceSamplesStore } from '@modules/study-insights/store';
import { PillId } from '@modules/study-insights/components/CollectionAndSequencing/CollectionAndSequencing.types';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { SHARED_TRANSLATION } from '@modules/shared/constants';
import { useSorting } from '@modules/shared/hooks';

import {
  StyledTableBodyCell,
  StyledTableBodyRow,
  StyledTableHeadRow,
  StyledNotResultsRowText,
  StyledNotResultsCellText,
  StyledTableHeadCell,
  StyledCaretUp,
  StyledCaretDown,
  StyledTableHeadDateReceivedCell,
  StyledTableHeadOrderIdCell,
  StyledError,
} from './ViewOrderIdsTable.styles';
import { ViewOrderIdsTableProps } from './ViewOrderIdsTable.types';
import { styled } from '@/stitches.config';

const PatientCell = styled('td', {
  padding: '$4 $0',
  fontSize: '$4',
  lineHeight: '$6',
  fontWeight: '$bold',
  textAlign: 'left',
  '&:first-child': {
    paddingLeft: '$8',
  },
  '&:last-child': {
    paddingRight: '$8',
  },
});

const ViewOrderIdsTable: FC<ViewOrderIdsTableProps> = ({ data, isError, isLoading, onChangeSorting }) => {
  const { t } = useTranslation([STUDY_INSIGHTS_TRANSLATION, SHARED_TRANSLATION]);
  const [statusDateName, setStatusDateName] = useState<string>('');
  const { sorting, handleSorting: setSorting, isColumnSorted, isAscendingSorted } = useSorting();
  const { selectedPill } = useSequenceSamplesStore();

  const handleSorting = (column: string): void => {
    setSorting(column);
    onChangeSorting(sorting);
  };

  useEffect(() => {
    let statusDateName = '';
    switch (selectedPill) {
      case PillId.SEQUENCED:
        statusDateName = t('view_order_id.status_date.sequencing_complete');
        break;
      case PillId.RECEIVED:
        statusDateName = t('view_order_id.status_date.sample_received');
        break;
      case PillId.AWAITING:
        statusDateName = t('view_order_id.status_date.order_received');
        break;
    }

    setStatusDateName(statusDateName);
  }, [selectedPill]);

  const colSpan = selectedPill !== PillId.AWAITING ? 6 : 5;

  const tableBody =
    !isLoading && data?.data?.length ? (
      data?.data.map((item, index) => (
        <StyledTableBodyRow css={{ backgroundColor: index % 2 ? '$white' : '$seafoam700' }} key={index}>
          <PatientCell>{item.patientId}</PatientCell>
          <StyledTableBodyCell>{item.orderId}</StyledTableBodyCell>
          <StyledTableBodyCell>{item.kitId}</StyledTableBodyCell>
          {selectedPill !== PillId.AWAITING && (
            <StyledTableBodyCell width="20%">
              {t(`view_order_id.type.${item.sampleType.toLowerCase()}`, { ns: STUDY_INSIGHTS_TRANSLATION })}
            </StyledTableBodyCell>
          )}
          <StyledTableBodyCell>{formatDate(item.statusDate, 'EEEE; MMM d, yyyy')}</StyledTableBodyCell>
        </StyledTableBodyRow>
      ))
    ) : (
      <StyledNotResultsRowText>
        <StyledNotResultsCellText colSpan={colSpan}>
          {isLoading ? (
            <Loader css={{ height: '570px' }} />
          ) : !isError ? (
            t('no_results', { ns: COMMON_TRANSLATION })
          ) : (
            <StyledError>{t('errors.generic', { ns: COMMON_TRANSLATION })}</StyledError>
          )}
        </StyledNotResultsCellText>
      </StyledNotResultsRowText>
    );

  return (
    <>
      <Box css={{ overflow: 'scroll', maxHeight: '50vh' }}>
        <Table css={{ orderCollapse: 'collapse', width: '100%' }}>
          <TableHead>
            <StyledTableHeadRow disabled={!isLoading && !data?.data?.length}>
              <StyledTableHeadCell css={{ cursor: 'auto' }}>{t('sample_tracking.patient_id')}</StyledTableHeadCell>
              <StyledTableHeadOrderIdCell css={{ width: '15%' }}>
                {t('sample_tracking.order_id')}
              </StyledTableHeadOrderIdCell>
              <StyledTableHeadCell css={{ width: '15%', cursor: 'auto' }}>
                {t('sample_tracking.kit_id')}
              </StyledTableHeadCell>
              {selectedPill !== PillId.AWAITING && (
                <StyledTableHeadCell css={{ width: '15%' }} onClick={() => handleSorting('sampleType')}>
                  {t('view_order_id.sample_type')}
                  {isColumnSorted('sampleType') && isAscendingSorted('sampleType') && <StyledCaretUp />}
                  {isColumnSorted('sampleType') && !isAscendingSorted('sampleType') && <StyledCaretDown />}
                </StyledTableHeadCell>
              )}
              <StyledTableHeadDateReceivedCell onClick={() => handleSorting('statusDate')}>
                {statusDateName && t(statusDateName)}
                {isColumnSorted('statusDate') && isAscendingSorted('statusDate') && <StyledCaretUp />}
                {isColumnSorted('statusDate') && !isAscendingSorted('statusDate') && <StyledCaretDown />}
              </StyledTableHeadDateReceivedCell>
            </StyledTableHeadRow>
          </TableHead>
          <TableBody>{tableBody}</TableBody>
        </Table>
      </Box>
    </>
  );
};

export default ViewOrderIdsTable;
